import { useCallback, useRef, useState } from 'react';

const MIN_COLUMN_WIDTH = 60;

/**
 * Drag-to-resize column widths for a `table-fixed` table.
 *
 * The table renders a `<colgroup>` from `widths` and puts a handle on each
 * header that calls `startResize(key)` on mousedown. Under fixed layout the
 * browser takes column widths from the `<col>` elements alone, so the state
 * here is the single source of truth for how wide every column is.
 *
 * Widths live in component state only, so a page refresh restores the
 * defaults — a resize is a per-visit adjustment, not a saved preference.
 *
 * Mouse events rather than pointer events: the move/up listeners sit on
 * `window` so a fast drag that outruns the handle keeps tracking, and jsdom
 * (the test DOM) does not carry `clientX` through synthetic pointer events.
 *
 * @param {Object<string, number>} defaults  Column key → default width in px.
 *                             Define it at module scope so its identity is stable.
 * @returns {{ widths: Object<string, number>,
 *             startResize: (key: string) => (e: MouseEvent) => void,
 *             resetColumn: (key: string) => void }}
 */
export function useResizableColumns(defaults) {
  const [widths, setWidths] = useState(defaults);

  // Read at mousedown so `startResize` does not have to be rebuilt (and every
  // header re-rendered) on each pixel of a drag.
  const widthsRef = useRef(widths);
  widthsRef.current = widths;

  const startResize = useCallback(
    (key) => (e) => {
      if (e.button !== 0) return;
      e.preventDefault(); // no text selection while dragging
      e.stopPropagation();
      const startX = e.clientX;
      const startWidth = widthsRef.current[key];

      const onMove = (ev) => {
        const next = Math.max(MIN_COLUMN_WIDTH, Math.round(startWidth + ev.clientX - startX));
        setWidths((w) => (w[key] === next ? w : { ...w, [key]: next }));
      };
      const onUp = () => {
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        // The browser follows mouseup with a click on the nearest common
        // ancestor of the down and up targets — the sortable <th> itself when
        // the drag ends inside the same header. Swallow that one click so a
        // resize never re-sorts. It fires in the same task as mouseup, so the
        // timeout removes the trap if no click comes (it would otherwise eat
        // the user's next real click).
        const swallow = (ev) => ev.stopPropagation();
        window.addEventListener('click', swallow, { capture: true, once: true });
        setTimeout(() => window.removeEventListener('click', swallow, { capture: true }), 0);
      };

      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    },
    [],
  );

  const resetColumn = useCallback(
    (key) => setWidths((w) => ({ ...w, [key]: defaults[key] })),
    [defaults],
  );

  return { widths, startResize, resetColumn };
}
